"use client";

import React, {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { SiteHeader, SiteFooter, SkipLink } from "../../patterns/navigation";
import type { SiteHeaderAccount } from "../../patterns/SiteHeader/SiteHeader";
import { useTranslate } from "../../lib/translation";
import { PageTransition } from "../animations/PageTransition";
import { DonnyActionProvider } from "../DonnyActionProvider";
import styles from "./NextLayout.module.css";

const ChatWidget = lazy(() => import("../ChatWidget/ChatWidget"));
const CookieConsentModal = lazy(() => import("../CookieConsent/CookieConsent"));

/**
 * Lets a routed page hide the floating chat widget for as long as it is
 * mounted. Provided by NextLayout, consumed by app/not-found.tsx: offering a
 * chat assistant on an error page is noise, not help.
 */
const ChatVisibilityContext = createContext<((hidden: boolean) => void) | null>(
  null,
);

/**
 * Hide the floating chat widget while the calling component is mounted.
 * Restores it automatically on unmount, so navigating away from the page
 * brings the widget back.
 */
export function useHideChatWidget(): void {
  const setHidden = useContext(ChatVisibilityContext);

  useEffect(() => {
    if (!setHidden) return undefined;
    setHidden(true);
    return () => setHidden(false);
  }, [setHidden]);
}

/**
 * Lets a routed area narrow the header's language switchers while it is
 * mounted. Provided by NextLayout, consumed by the client intake, which is
 * offered in EN and FI only.
 */
const LanguageOptionsContext = createContext<
  ((codes: readonly string[] | undefined) => void) | null
>(null);

/**
 * Offer only these languages in the site header while the calling component
 * is mounted. Restores every site language on unmount.
 */
export function useLimitedLanguages(codes: readonly string[]): void {
  const setCodes = useContext(LanguageOptionsContext);
  const key = codes.join(",");

  useEffect(() => {
    if (!setCodes) return undefined;
    setCodes(key.split(","));
    return () => setCodes(undefined);
  }, [setCodes, key]);
}

/** Lets an account area show the signed-in person in the site header. */
const HeaderAccountContext = createContext<
  ((account: SiteHeaderAccount | undefined) => void) | null
>(null);

/**
 * Show this person, with their account menu, in the site header while the
 * calling component is mounted. Removed again on unmount.
 */
export function useHeaderAccount(account: SiteHeaderAccount | undefined): void {
  const setAccount = useContext(HeaderAccountContext);

  useEffect(() => {
    if (!setAccount) return undefined;
    setAccount(account);
    return () => setAccount(undefined);
  }, [setAccount, account]);
}

function ClientOnly({
  children,
  defer = false,
}: {
  children: ReactNode;
  /**
   * Wait for the load event and an idle moment before mounting. The chat
   * widget brings the AI SDK, zod and the markdown renderer (about 270 KB of
   * script); fetched during hydration it competed with the page for the main
   * thread on phones.
   */
  defer?: boolean;
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!defer) {
      setMounted(true);
      return undefined;
    }
    let idle: number | undefined;
    const mount = () => {
      idle = window.requestIdleCallback
        ? window.requestIdleCallback(() => setMounted(true), { timeout: 3000 })
        : window.setTimeout(() => setMounted(true), 1500);
    };
    if (document.readyState === "complete") mount();
    else window.addEventListener("load", mount, { once: true });
    return () => {
      window.removeEventListener("load", mount);
      if (idle !== undefined) {
        if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
        window.clearTimeout(idle);
      }
    };
  }, [defer]);

  if (!mounted) return null;

  return <Suspense fallback={null}>{children}</Suspense>;
}

/** Props for NextLayout. */
export interface NextLayoutProps {
  /** Additional class names on the layout wrapper. */
  className?: string;
  children: React.ReactNode;
}

/**
 * NextLayout component.
 */
export function NextLayout({ children, className }: NextLayoutProps) {
  // The skip link is the first focusable element on every page, so it must
  // speak the visitor's language (EN/FI/SV) rather than the SkipLink default.
  const t = useTranslate();
  const [chatHidden, setChatHidden] = useState(false);
  const [account, setAccount] = useState<SiteHeaderAccount | undefined>();
  const [languageCodes, setLanguageCodes] = useState<
    readonly string[] | undefined
  >();

  return (
    <ChatVisibilityContext.Provider value={setChatHidden}>
      <LanguageOptionsContext.Provider value={setLanguageCodes}>
        <HeaderAccountContext.Provider value={setAccount}>
          <DonnyActionProvider>
            <div
              className={
                className ? `${styles.layout} ${className}` : styles.layout
              }
            >
              <SkipLink href="#main-content">
                {t("skipToMainContent", "Skip to main content")}
              </SkipLink>
              <SiteHeader languageCodes={languageCodes} account={account} />
              {/* tabIndex=-1 makes the skip-link target programmatically
              focusable, so activating the skip link moves focus INTO main
              (not just scrolls). Without it the browser leaves focus on
              <body> and the next Tab restarts from the header — defeating
              the skip link (WCAG 2.4.1). */}
              <main id="main-content" tabIndex={-1} className={styles.main}>
                <PageTransition>{children}</PageTransition>
              </main>
              <SiteFooter />
              {!chatHidden && (
                <ClientOnly defer>
                  <ChatWidget />
                </ClientOnly>
              )}
            </div>
          </DonnyActionProvider>
          <ClientOnly>
            <CookieConsentModal />
          </ClientOnly>
        </HeaderAccountContext.Provider>
      </LanguageOptionsContext.Provider>
    </ChatVisibilityContext.Provider>
  );
}
